import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { IdPersonalizable } from 'src/app/dashboard/models/idPersonalizable';
import { Parametros } from 'src/app/dashboard/models/parametros';
import { ConfiguracionAvanzadaService } from 'src/app/dashboard/services/configuracion-avanzada.service';
import { AuthService } from 'src/app/shared/services/auth.service';

@Component({
  selector: 'app-id-personalizable',
  templateUrl: './id-personalizable.component.html',
  styleUrls: ['./id-personalizable.component.css']
})
export class IdPersonalizableComponent implements OnInit {

  idPersonalizable: IdPersonalizable = {
    identifier_characters: '',
    include_characters: '',
    identifier_year: '',
    include_year: '',
    personalized_identifier: '',
    reset_numbering: '',
    custom_reset_date: '',
    custom_reset_date_value: ''

  };

  parametros: Parametros | Parametros[]; // Parameters fetched from the backend

  validatingForm: FormGroup;

  id_personalizable_enabled: boolean = false;
  include_charactersF: boolean = false;
  identifier_charactersF: string = '';
  include_yearF: boolean = false;
  identifier_yearF: string = '';
  reset_numberingF: boolean = false;
  custom_reset_dateF: boolean = false;
  custom_reset_date_valueF: string = '';
  currentYear: number = new Date().getFullYear();
  checkResetDisabled: boolean = false;
  showAdvOpts: boolean = false;
  minDate: string = this.getMinDate();

  // Helper method to get the value of a parameter by its ID from the parametros array
  private getParamValue(paramId: string): string {
    if (Array.isArray(this.parametros)) {
      return this.parametros.find((param) => param.id_parametro === paramId)?.valor ?? '';
    }
    return this.parametros?.id_parametro === paramId ? this.parametros.valor : '';
  }

  // Helper method to compute the identifier year based on a custom reset date
  private getIdentifierYearFromResetDate(dateValue: string): string | null {
    if (!dateValue) {
      return null;
    }
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const yearEnd = date.getFullYear();
    const yearStart = yearEnd - 1;
    const shortStart = String(yearStart % 100).padStart(2, '0');
    const shortEnd = String(yearEnd % 100).padStart(2, '0');
    return `${shortStart}-${shortEnd}`;
  }

  // Helper method to determine the default identifier year value when enabling the year input
  private resolveIdentifierYearValue(): string {
    if (this.custom_reset_dateF && this.custom_reset_date_valueF) {
      return this.getIdentifierYearFromResetDate(this.custom_reset_date_valueF) || this.currentYear.toString();
    }
    return this.currentYear.toString();
  }

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private configuracionAvanzadaService: ConfiguracionAvanzadaService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router,
    private cdRef: ChangeDetectorRef
  ) {
    // Initialize the form group with controls and their validators.
    this.validatingForm = this.fb.group({
      include_characters: [false],
      identifier_characters: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^[A-Za-z]*$/), Validators.maxLength(7)]],
      include_year: [false],
      identifier_year: [{ value: '', disabled: true }],
      include_numbers: [true],
      reset_numbering: [false, this.resetNumberingValidator.bind(this)],
      custom_reset_date: [{ value: false, disabled: true }],
      custom_reset_date_value: [{ value: '', disabled: true }, [this.futureDateValidator.bind(this)]]

    });
  }

  /**
   * Fetches initial parameter values from the backend and populates the component data.
   */
  ngOnInit(): void {
    let language = this.auth.getLanguage();
    if (language === '' || language === null) {
      language = 'es';
    }

    this.showAdvOpts = this.auth.getShowAdvOpts();

    this.validatingForm.get('include_numbers').disable();

    // Fetch advanced parameters and set the component's data.
    this.configuracionAvanzadaService
      .searchAdvancedParam(this.auth.getSociedad())
      .subscribe({
        next: (response) => {
          if (response.success && response.data && response.code === 200) {
            this.parametros = response.data;

            this.identifier_charactersF = this.getParamValue('identifierCharacters');
            this.identifier_yearF = this.getParamValue('identifierYear') || this.currentYear.toString();

            this.id_personalizable_enabled = this.getParamValue('idPersonalizableEnabled') === '1';
            this.include_charactersF = this.getParamValue('includeCharacters') === '1';
            this.include_yearF = this.getParamValue('includeYear') === '1';
            this.reset_numberingF = this.getParamValue('resetNumbering') === '1';
            this.custom_reset_dateF = this.getParamValue('customResetDate') === '1';
            this.custom_reset_date_valueF = this.getParamValue('customResetDateValue') || '';

            if (this.custom_reset_dateF && this.custom_reset_date_valueF) {
              const computedIdentifierYear = this.getIdentifierYearFromResetDate(this.custom_reset_date_valueF);
              if (computedIdentifierYear) {
                this.identifier_yearF = computedIdentifierYear;
              }
            } else {
              this.identifier_yearF = this.currentYear.toString();
            }

            this.toggleInput('include_characters', this.include_charactersF, 'identifier_characters');
            this.toggleInput('include_year', this.include_yearF, 'identifier_year');

            this.disableChecks();
            this.disableResetNumbering();
          } else if (response.code === 404 && !response.success){
            this.toastr.error(this.translate.instant('texto_899'));
            this.identifier_charactersF = '';
            this.identifier_yearF = this.currentYear.toString();
            this.id_personalizable_enabled = false;
            this.include_charactersF = false;
            this.include_yearF = false;
            this.reset_numberingF = false;
            this.custom_reset_dateF = false;
            this.custom_reset_date_valueF = '';
          }
        },
        error: (err) => {
          this.toastr.error(this.translate.instant('texto_813'), this.translate.instant('texto_890'));
          this.identifier_charactersF = '';
          this.identifier_yearF = this.currentYear.toString();
          this.id_personalizable_enabled = false;
          this.include_charactersF = false;
          this.include_yearF = false;
          this.reset_numberingF = false;
          this.custom_reset_dateF = false;
          this.custom_reset_date_valueF = '';
        }
      });

  }

  /**
   * Disable checks when check reset numbering is true,
   * this way is required add characters and year.
   */
  disableChecks() {
    if (this.reset_numberingF === true) {
      this.include_yearF = true;
      this.validatingForm.get('include_year').disable();
      this.validatingForm.get('custom_reset_date').enable();
      this.toggleInput('include_year', this.include_yearF, 'identifier_year');
      
      // Enable custom_reset_date_value if custom_reset_dateF is true
      if (this.custom_reset_dateF) {
        this.validatingForm.get('custom_reset_date_value').enable();
        if (this.custom_reset_date_valueF) {
          this.validatingForm.get('custom_reset_date_value').setValue(this.custom_reset_date_valueF);
        }
        this.syncIdentifierYearFromCustomResetDate();
      }
      
      this.updateIdentifier();
    } else {
      this.reset_numberingF = false;
      this.custom_reset_dateF = false;
      this.custom_reset_date_valueF = '';
      this.identifier_yearF = this.currentYear.toString();
      this.validatingForm.get('include_year').enable();
      this.validatingForm.get('custom_reset_date').disable();
      this.validatingForm.get('custom_reset_date').setValue(false);
      this.validatingForm.get('custom_reset_date_value').disable();
      this.validatingForm.get('custom_reset_date_value').setValue('');
      this.validatingForm.get('identifier_year')?.setValue(this.identifier_yearF);
    }

    if (this.checkResetDisabled == true && this.validatingForm.value.reset_numbering == false) {
      this.toastr.error(
        this.translate.instant('texto_291'),
        this.translate.instant('texto_763')
      );
    }
    // changes review
    this.cdRef.detectChanges();
  }

  /**
   * Custom validator to check if reset numbering can be enabled based on the presence of complaints in the current year.
   */
  resetNumberingValidator(control: AbstractControl): ValidationErrors | null {
    if (this.checkResetDisabled && control.value === false) {
      return { resetNotAllowed: false };
    }
    return null;
  }


  /**
   * Validates that the selected date is in the future
   */
  futureDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate <= today) {
      return { notFutureDate: true };
    }
    return null;
  }

  /**
   * Returns today's date in ISO format (YYYY-MM-DD) to use as minDate in date input
   */
  getMinDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Handles the change event for custom_reset_date checkbox
   * Shows/hides the date input field
   */
  toggleCustomDateInput() {
    const customDateControl = this.validatingForm.get('custom_reset_date_value');
    if (this.custom_reset_dateF) {
      customDateControl?.enable();
      customDateControl?.setValue('');
    } else {
      customDateControl?.disable();
      customDateControl?.setValue('');
      this.identifier_yearF = this.currentYear.toString();
      if (this.include_yearF) {
        this.validatingForm.get('identifier_year')?.setValue(this.identifier_yearF);
      }
    }
    this.syncIdentifierYearFromCustomResetDate(); // Sync the identifier year with the custom reset date change
    this.cdRef.detectChanges(); // Trigger change detection to update the view immediately after toggling the custom date input
  }

  /**
   * @description Synchronizes the identifier year with the custom reset date value when the custom reset date is changed.
   * If a valid year can be computed from the custom reset date, it updates the identifier year form control and the component's identifier_yearF property.
   * This ensures that the identifier year reflects the correct value based on the selected custom reset date.
   */
  syncIdentifierYearFromCustomResetDate() {
    if (this.custom_reset_dateF && this.custom_reset_date_valueF) {
      const computed = this.getIdentifierYearFromResetDate(this.custom_reset_date_valueF);
      if (computed) {
        this.identifier_yearF = computed;
        if (this.include_yearF) {
          this.validatingForm.get('identifier_year')?.setValue(computed);
        }
        this.updateIdentifier();
      }
      return;
    }
    this.identifier_yearF = this.currentYear.toString();
    if (this.include_yearF) {
      this.validatingForm.get('identifier_year')?.setValue(this.identifier_yearF);
    }
    this.updateIdentifier();
  }

  /**
   * Toggles the enabled/disabled state of a form control based on the state of a related checkbox.
   * Clears the input field and updates the identifier if the checkbox is unchecked.
   * @param includeControlName - The name of the checkbox form control.
   * @param identifierControlName - The name of the input form control to toggle.
   */
  toggleInput(includeControlName: string, includeModel: boolean, identifierControlName: string) {
    const includeControl = this.validatingForm.get(includeControlName);
    const identifierControl = this.validatingForm.get(identifierControlName);

    if (includeControl?.value || includeModel) {
      identifierControl?.enable();
      if (identifierControlName === 'identifier_year' && !identifierControl?.value) {
        identifierControl?.setValue(this.resolveIdentifierYearValue()); // Set default value when enabled
        this.updateIdentifier();
      }
    } else {
      identifierControl?.disable();
      identifierControl?.setValue('');
      this.updateIdentifier();
    }
  }

  /**
   * Updates the personalized identifier based on the current form values.
   * Joins the input values with an underscore if they are included.
   */
  updateIdentifier() {
    const formValues = this.validatingForm.value;
    const parts: string[] = [];

    if (formValues.include_characters && formValues.identifier_characters) {
      parts.push(formValues.identifier_characters);
    } else if (this.include_charactersF) {
      parts.push(this.identifier_charactersF);
    }
    if (formValues.include_year && formValues.identifier_year || this.include_yearF) {
      parts.push(formValues.identifier_year || this.identifier_yearF);
    }
    parts.push('0001');


    this.idPersonalizable.personalized_identifier = parts.join('-');
  }


  /**
   * Validate if disable reset numberig is available
   * If there are complaints in the current year, that option won't be possible.
   */
  disableResetNumbering() {
    this.configuracionAvanzadaService.searchComplaintsByYear().subscribe(res => {
      if (res.areThereComplaints == 1 && this.reset_numberingF == true) {
        this.checkResetDisabled = true;
      } else {
        this.checkResetDisabled = false;
      }
    }, error => {
      console.error("Error al llamar al endpoint searchComplaintsByYear", error);
    });
  }

  /**
   * Submits the form data to the backend service to update the identifier parameters.
   * Shows a success or error message based on the response.
   */
  submitIdentifier() {

    // Validate that if custom_reset_date is checked, a date must be provided
    if (this.validatingForm.value.custom_reset_date == true && !this.validatingForm.value.custom_reset_date_value) {
      this.toastr.error(
        this.translate.instant('texto_914'),
        this.translate.instant('texto_813') || 'Error'
      );
      return;
    }

    // Validate that the date is in the future (if custom_reset_date is checked)
    if (this.validatingForm.value.custom_reset_date == true) {
      const dateControl = this.validatingForm.get('custom_reset_date_value');
      if (dateControl && dateControl.invalid) {
        this.toastr.error(
          this.translate.instant('texto_914'),
          this.translate.instant('texto_813') || 'Error'
        );
        return;
      }
    }

    if (this.validatingForm.value.include_characters == true) {
      this.idPersonalizable.include_characters = '1';
    } else {
      this.idPersonalizable.include_characters = '0';
    }

    if (this.validatingForm.value.include_year == true || this.include_yearF == true) {
      this.idPersonalizable.include_year = '1';
    } else {
      this.idPersonalizable.include_year = '0';
    }

    if (this.validatingForm.value.reset_numbering == true) {
      this.idPersonalizable.reset_numbering = '1';
    } else {
      this.idPersonalizable.reset_numbering = '0';
    }

    if (this.validatingForm.value.custom_reset_date == true) {
      this.idPersonalizable.custom_reset_date = '1';
    } else {
      this.idPersonalizable.custom_reset_date = '0';
    }

    // Set the custom reset date value (empty string if not set)
    this.idPersonalizable.custom_reset_date_value = this.validatingForm.value.custom_reset_date_value || '';

    const identifierCharactersToCheck = this.idPersonalizable.include_characters === '1'
      ? (this.validatingForm.value.identifier_characters || this.identifier_charactersF || '')
      : '';
    const identifierYearToCheck = this.idPersonalizable.include_year === '1'
      ? (this.validatingForm.value.identifier_year || this.identifier_yearF || this.currentYear.toString())
      : '';
    const customResetDateValueToCheck = this.idPersonalizable.custom_reset_date === '1'
      ? (this.idPersonalizable.custom_reset_date_value || '')
      : '';

      /**
       * Check if the identifier configuration is unique before updating.
       */
    this.configuracionAvanzadaService
      .checkIdentifierUnique(
        this.auth.getSociedad(),
        identifierCharactersToCheck,
        identifierYearToCheck,
        customResetDateValueToCheck
      )
      .subscribe({
        next: (checkRes) => {
          /**
           * If the identifier configuration already exists, show an error and do not proceed with the update.
           */
          const exists = (checkRes as any)?.data?.exists === true || (checkRes as any)?.exists === true;
          const societies = (checkRes as any)?.data?.societies || [];
          if (exists) {
            const societiesText = societies.length
              ? `${societies.join(', ')}`
              : '';
            this.toastr.error(
              this.translate.instant('texto_959') + societiesText,
              this.translate.instant('texto_813') || 'Error'
            );
            return;
          }

          this.configuracionAvanzadaService.updateIdentifierParams(
            this.auth.getSociedad(),
            this.idPersonalizable.include_characters,
            this.validatingForm.value.identifier_characters,
            this.idPersonalizable.include_year,
            (this.validatingForm.value.identifier_year || this.identifier_yearF || this.currentYear.toString()),
            this.idPersonalizable.reset_numbering,
            this.idPersonalizable.custom_reset_date,
            this.idPersonalizable.custom_reset_date_value
          ).subscribe(res => {
            if ((res as any).success === true) {
              this.toastr.success(
                this.translate.instant('texto_290'),
                this.translate.instant('texto_292')
              );
            } else {
              this.toastr.error(
                this.translate.instant('texto_291'),
                this.translate.instant('texto_292')
              );
            }
            this.router
              .navigateByUrl('/RefreshComponent', { skipLocationChange: true })
              .then(() => {
                this.router.navigate(['/dashboard/layout/advanced-setting']);
              });
          },
            err => {
              if (err.status === 409) {
                this.toastr.error(this.translate.instant('texto_291'), this.translate.instant('texto_813'));
              } else {
                this.toastr.error(this.translate.instant('texto_291'), this.translate.instant('texto_813'));
              }
            });
        },
        error: () => {
          this.toastr.error(this.translate.instant('texto_291'), this.translate.instant('texto_813'));
        }
      });
  }
}
